/* eslint-disable no-console */
import type { ChatSurface } from '@/composables/analytics/analytics-service';
import type {
   ApalumaChatMessage,
   ApalumaChatThread,
   ChatMessageFeedback,
   ChatMessageResponse,
   ChatType,
} from '@/types/api-response/chat-session-types.ts';
import { defineStore } from 'pinia';
import { computed, reactive, ref, shallowReactive } from 'vue';
import { useAnalytics } from '@/composables/analytics/analytics-service';
import { useUserGeolocation } from '@/composables/use-user-geolocation.ts';
import { useWebSocketChat } from '@/composables/use-websocket-chat.ts';
import {
   createThread as apiCreateThread,
   deleteThread as apiDeleteThread,
   getThread as apiGetThread,
   listThreads as apiListThreads,
   sendMessage as apiSendMessage,
   updateThread as apiUpdateThread,
} from '@/services/chat-threads-service';
import { generateThreadTitle } from '@/services/llm-service';
import { useAuthStore } from '@/stores/auth-store.ts';

export interface CreateSessionOptions {
   chat_type?: ChatType;
   document_id?: string;
}

export interface SendMessageOptions {
   documentId?: string;
   filename?: string;
   /**
    * Which input the question came from, reported on `chat:ask`.
    *
    * Required, because this store is the only place the ask is recorded and an
    * unlabelled one is what the property exists to stop. Callers state it
    * rather than having it inferred from the URL, which records where the
    * browser happened to be rather than what the user clicked.
    */
   surface: ChatSurface;
   /**
    * The screen the question was typed on, where the sender navigated away from
    * it before this store got the chance to report.
    *
    * Left unset by callers that ask in place, which is most of them: the router
    * still knows the answer at that point and analytics reads it directly.
    */
   screen?: string;
}

export const useChatSessionStore = defineStore('chat-sessions', () => {
   const authStore = useAuthStore();
   const wsChat = useWebSocketChat();
   const { trackEvent } = useAnalytics();
   const { getLocationForChat } = useUserGeolocation();

   const SESSION_PAGE_SIZE = 25;

   const sessionState = reactive({
      sessions: [] as ApalumaChatThread[],
      total: 0,
      offset: 0,
      isLoading: false,
      error: null as string | null,
      titleFilter: '',
   });

   let sessionsAbortController: AbortController | null = null; // For canceling previous requests

   const hasMoreSessions = computed(() => sessionState.offset + SESSION_PAGE_SIZE < sessionState.total);

   // Optional: Check if we have loaded any data yet
   const isSessionsLoaded = computed(() => sessionState.sessions.length > 0);

   /* ------------------------------------------------------------------------------------------- */
   /**
    * Session messages (per selected chat session)
    */
   const SESSION_MESSAGES_PAGE_SIZE = 40;

   const selectedSession = ref<ApalumaChatThread | null>(null);

   const sessionMessages = ref<ApalumaChatMessage[]>([]);
   const sessionMessagesOffset = ref(0);
   const isSessionMessagesLoading = ref(false);
   const sessionMessagesError = ref<string | null>(null);

   // Set while the global floating Luma assistant is open. The assistant reuses
   // this shared session state (it briefly clears/reuses selectedSession), so this
   // flag lets the routed chat view skip its "session cleared -> redirect" reaction
   // while the assistant is driving the store.
   // TODO: give the floating assistant its own session state so this coupling —
   // and this flag — can go away. See luma-floating.vue (CodeRabbit #1).
   const floatingAssistantActive = ref(false);

   let sessionMessagesAbortController: AbortController | null = null;

   /**
    * A turn Luma is still working on, keyed by thread.
    *
    * The browser is what persists an answer, so a turn has to outlive the screen
    * that started it: leaving the chat, opening another thread, or starting a
    * second conversation must not end it. Keeping the live placeholder here
    * (rather than only in `sessionMessages`) means an answer can arrive for a
    * thread that isn't on screen without being written into whichever thread
    * is — temp ids are per-list, so that would otherwise land on a stranger's
    * message.
    */
   interface InFlightTurn {
      /** Distinguishes this turn from a later one on the same thread. */
      token: number;
      assistantTempId: number;
      /** Canonical live state for the streaming answer. */
      message: ApalumaChatMessage;
      abort: AbortController;
   }

   /**
    * `shallowReactive` on purpose. A deep `reactive` Map hands back a *proxy* of
    * the stored turn, so `map.get(id) === turn` is false against the object we
    * put in — which silently defeated the supersede checks below. It also
    * spared every `isThreadBusy` reader a re-render on each progress frame.
    */
   const inFlight = shallowReactive(new Map<number, InFlightTurn>());

   /**
    * Abort controllers for turns that have not reached `inFlight` yet.
    *
    * A turn only lands in `inFlight` once its HTTP leg has returned, so until
    * then a second send on the same thread found nothing to supersede: both
    * turns pushed optimistic messages, both opened a WebSocket turn, and the
    * first turn's placeholder ended as `status: 'error'` instead of being
    * removed. The window is not theoretical — `getLocationForChat()` can sit on a
    * browser permission prompt, and the send control stays enabled through it
    * because the messages that gate it have not been pushed yet.
    *
    * Plain Map, not reactive: nothing renders from it.
    */
   const pendingSends = new Map<number, AbortController>();
   let nextTurnToken = 1;

   /** Whether a thread has a turn still running — true even when off screen. */
   function isThreadBusy(threadId: number): boolean {
      return inFlight.has(threadId);
   }

   /**
    * Apply an edit to a message, but only while its thread is the visible one.
    *
    * `sessionMessages` holds the selected thread and nothing else, and temp ids
    * are positional within a list — so writing to it blind is how a background
    * answer ends up overwriting an unrelated message. Off-screen threads keep
    * their state in `inFlight` and are re-read from the API on return.
    */
   function patchMessage(
      threadId: number,
      messageId: number,
      update: (current: ApalumaChatMessage) => ApalumaChatMessage,
   ) {
      if (selectedSession.value?.id !== threadId)
         return;
      const idx = sessionMessages.value.findIndex(m => m.id === messageId);
      if (idx !== -1)
         sessionMessages.value[idx] = update(sessionMessages.value[idx]);
   }

   /** Drop a turn's optimistic messages, if its thread is still on screen. */
   function removeTempMessages(threadId: number, ...ids: number[]) {
      if (selectedSession.value?.id !== threadId)
         return;
      sessionMessages.value = sessionMessages.value.filter(m => !ids.includes(m.id));
   }

   const hasMoreSessionMessages = computed(() => {
      if (!selectedSession.value?.message_count) {
         return false;
      }
      return sessionMessagesOffset.value + SESSION_MESSAGES_PAGE_SIZE < selectedSession.value.message_count;
   });

   /**
    * Fetch chat sessions.
    * @param forceReset If true, clears current list and fetches from scratch.
    */
   async function fetchSessions(forceReset: boolean = false) {
      // Cancel previous request if it exists
      if (sessionsAbortController) {
         sessionsAbortController.abort();
      }

      // Create a new AbortController for this request
      sessionsAbortController = new AbortController();
      const currentAbortController = sessionsAbortController;

      try {
         sessionState.isLoading = true;
         sessionState.error = null;

         if (forceReset) {
            sessionState.offset = 0;
         }

         // const trimmedFilter = sessionState.titleFilter.trim();

         const response = await apiListThreads({
            limit: SESSION_PAGE_SIZE,
            offset: sessionState.offset,
            chat_type: 'luma_chat',
            keyword: sessionState.titleFilter,
         }, currentAbortController.signal);

         // Check if this request was aborted
         if (currentAbortController.signal.aborted) {
            return false;
         }

         if (forceReset) {
            // Replace all sessions when resetting (initial load or search)
            sessionState.sessions = response.threads;
            sessionState.total = response.total;
         }
         else {
            // Append new sessions for pagination (load more)
            sessionState.sessions = [...sessionState.sessions, ...response.threads];
         }

         sessionState.total = response.total;

         // Update selectedSession if it exists in the updated sessions list
         if (selectedSession.value) {
            const updatedSession = sessionState.sessions.find(
               s => s.id === selectedSession.value?.id,
            );
            if (updatedSession) {
               selectedSession.value = updatedSession;
            }
         }
         return true;
      }
      catch (e: any) {
         // Don't show error if request was aborted
         if (e?.name === 'AbortError' || currentAbortController.signal.aborted) {
            return false;
         }

         console.error(e);
         sessionState.error = 'Failed to fetch chat sessions';
         return false;
      }
      finally {
         // Only update loading state if this is still the current request
         if (currentAbortController === sessionsAbortController) {
            sessionState.isLoading = false;
         }
      }
   }

   /**
    * Search chat sessions by title filter.
    * @param query
    */
   async function searchSessions(query: string) {
      sessionState.titleFilter = query;
      sessionState.sessions = [];
      sessionState.total = 0;
      sessionState.offset = 0; // Reset pagination when searching
      await fetchSessions(true);
   }

   /**
    * Create a new chat session.
    * @param options - Options for creating the session (chat_type, document_id)
    * @returns The created session
    */
   async function createSession(options: CreateSessionOptions = {}): Promise<ApalumaChatThread | null> {
      const { chat_type = 'luma_chat', document_id } = options;

      try {
         const response = await apiCreateThread({
            chat_type,
            ...(document_id && { document_id }),
         });

         // A brand-new thread has no messages, so anything still in the store
         // belongs to the thread we just left. Clearing here rather than relying
         // on the route watcher matters because `selectedSession` is set below:
         // `syncFromRoute` short-circuits once it matches, so it would never
         // clear them. Stale messages made `sendMessage` believe the first
         // message wasn't the first, which skipped auto-titling the thread.
         sessionMessages.value = [];
         sessionMessagesOffset.value = 0;
         sessionMessagesError.value = null;

         // Only add to sessions list for luma_chat (not document chats)
         if (chat_type === 'luma_chat') {
            sessionState.sessions.unshift({
               ...response,
               title: 'New Session',
            });
         }

         // Set as selected session for all chat types (needed for document chat polling)
         selectedSession.value = response;

         return response;
      }
      catch (e) {
         console.error(e);
         return null;
      }
   }

   /**
    * Update the session title on the first message.
    */
   function updateSessionTitleOnFirstMessage(session: ApalumaChatThread) {
      if (!session.title)
         return;

      const sessionToUpdate = sessionState.sessions.find(s => s.id === session.id);
      if (sessionToUpdate) {
         sessionToUpdate.title = session.title;
      }

      if (selectedSession.value?.id === session.id) {
         selectedSession.value.title = session.title;
      }
   }

   /**
    * Load more chat sessions (pagination).
    */
   async function loadMoreSessions() {
      if (!hasMoreSessions.value || sessionState.isLoading) {
         return;
      }

      sessionState.offset += SESSION_PAGE_SIZE;
      await fetchSessions(false);
   }

   /**
    * Delete a chat session.
    */
   async function deleteSession(sessionId: number) {
      try {
         await apiDeleteThread(sessionId);

         // The one case where dropping a running turn is right: there is no
         // longer a thread to persist its answer to.
         inFlight.get(sessionId)?.abort.abort();
         inFlight.delete(sessionId);
         pendingSends.get(sessionId)?.abort();
         pendingSends.delete(sessionId);
         wsChat.closeThread(sessionId);

         // Update the local state immediately for UI responsiveness
         sessionState.sessions = sessionState.sessions.filter(s => s.id !== sessionId);
         sessionState.total = Math.max(0, sessionState.total - 1);

         // If the deleted session is the currently selected one, clear selection and redirect
         if (selectedSession.value?.id === sessionId) {
            clearSelectedSession();
         }

         return true;
      }
      catch (e) {
         console.error(e);
         return false;
      }
   }

   /**
    * Rename a chat session.
    */
   async function renameSession(sessionId: number, title: string) {
      try {
         const response = await apiUpdateThread(sessionId, {
            title,
            is_archived: false,
         });

         // Update the local state immediately for UI responsiveness
         const session = sessionState.sessions.find(s => s.id === response.id);
         if (session) {
            session.title = title;
         }

         // If the renamed session is the currently selected one, update selectedSession
         if (selectedSession.value?.id === response.id) {
            selectedSession.value.title = title;
         }

         return true;
      }
      catch (e) {
         console.error(e);
         return false;
      }
   }

   /**
    * Fetch chat messages for a specific session.
    */
   async function fetchSessionMessages(sessionId: number, forceReset: boolean = false) {
      // Cancel previous request if it exists
      if (sessionMessagesAbortController) {
         sessionMessagesAbortController.abort();
      }

      sessionMessagesAbortController = new AbortController();
      const currentAbortController = sessionMessagesAbortController;

      try {
         isSessionMessagesLoading.value = true;
         sessionMessagesError.value = null;

         if (forceReset) {
            sessionMessages.value = [];
            sessionMessagesOffset.value = 0;
         }

         const response = await apiGetThread(sessionId, {
            limit: SESSION_MESSAGES_PAGE_SIZE,
            offset: sessionMessagesOffset.value,
         }, currentAbortController.signal);

         if (currentAbortController.signal.aborted) {
            return false;
         }

         // Update the selected session with the thread details from the response (omitting messages)
         const { messages, ...threadDetails } = response;
         selectedSession.value = threadDetails;

         // Backend returns DESC (newest first) and we want to keep the isInitializedDone = true for the first message
         const messagesFromServer = response.messages.reverse().map(m => ({ ...m, isInitializedDone: true })) || [];

         // Replace vs prepend (for infinite scroll at top)
         if (forceReset || sessionMessagesOffset.value === 0) {
            // Initial load
            sessionMessages.value = messagesFromServer;
         }
         else {
            // Infinite scroll: prepend older messages at the beginning
            sessionMessages.value = [...messagesFromServer, ...sessionMessages.value];
         }

         // A turn that kept running while this thread was off screen has no
         // saved assistant row yet, so the fetch can't return one. Put the live
         // placeholder back rather than showing a question with no answer.
         const running = inFlight.get(sessionId);
         if (running && !sessionMessages.value.some(m => m.id === running.assistantTempId)) {
            sessionMessages.value = [...sessionMessages.value, running.message];
         }

         return true;
      }
      catch (e: any) {
         if (e?.name === 'AbortError' || currentAbortController.signal.aborted) {
            return false;
         }
         console.error(e);
         sessionMessagesError.value = 'Failed to fetch chat messages';
         return false;
      }
      finally {
         if (currentAbortController === sessionMessagesAbortController) {
            isSessionMessagesLoading.value = false;
         }
      }
   }

   async function loadMoreSessionMessages() {
      if (!hasMoreSessionMessages.value || isSessionMessagesLoading.value)
         return;

      sessionMessagesOffset.value += SESSION_MESSAGES_PAGE_SIZE;

      if (selectedSession.value?.id) {
         await fetchSessionMessages(selectedSession.value.id, false);
      }
   }

   /**
    * Clear the selected session and all related data
    */
   function clearSelectedSession() {
      // Deliberately does NOT touch the socket. Clearing the selection is a
      // navigation event; a turn in flight belongs to its thread, not to the
      // screen, and the browser is the only thing that will persist its answer.
      selectedSession.value = null;
      sessionMessages.value = [];
      sessionMessagesOffset.value = 0;
      sessionMessagesError.value = null;
      isSessionMessagesLoading.value = false;
      if (sessionMessagesAbortController) {
         sessionMessagesAbortController.abort();
         sessionMessagesAbortController = null;
      }
   }

   /**
    * Selects a session and fetches the messages for the session
    * @param sessionId - The session ID to select
    * @param fetchMessages - Whether to fetch the messages for the session
    */
   async function selectSession(sessionId: number, fetchMessages: boolean = true) {
      // Switching threads leaves the one you came from running.
      // Find the session from the sessions list
      const session = sessionState.sessions.find(s => s.id === sessionId);
      if (session) {
         selectedSession.value = session;
      }
      if (fetchMessages) {
         await fetchSessionMessages(sessionId, true);
      }
   }

   /**
    * Syncs store selection from route param
    * - If `routeSessionId` is provided and different, it fetches that session.
    * - If `routeSessionId` is not provided, it clears the selected session.
    * @param routeSessionId - The session ID from the route
    * @param fetchMessages - Whether to fetch the messages for the session
    */
   async function syncFromRoute(routeSessionId: string | null, fetchMessages: boolean = true) {
      const sessionId = Number(routeSessionId);

      if (!sessionId) {
         clearSelectedSession();
         return;
      }
      // If we're already on this session and have messages, don't refetch (preserves immediately added messages)
      if (selectedSession.value?.id === sessionId && sessionMessages.value.length > 0) {
         return;
      }
      if (selectedSession.value?.id !== sessionId) {
         await selectSession(sessionId, fetchMessages);
      }
   }

   /**
    * Sends a message to the AI chatbot
    * @param message - The message to send
    * @param sessionId - The session ID to send the message to
    * @param options - Where the question was asked (`surface`, required) plus any
    * document metadata (documentId, filename)
    * @returns The response from the API
    */
   async function sendMessage(message: string, sessionId: number, options: SendMessageOptions): Promise<ChatMessageResponse | null> {
      // Only this thread's previous turn is superseded. Other conversations
      // keep running.
      inFlight.get(sessionId)?.abort.abort();
      pendingSends.get(sessionId)?.abort();
      const localAbortController = new AbortController();
      // Claim the thread before the first await, so the next send can supersede
      // this turn while it is still in its HTTP phase.
      pendingSends.set(sessionId, localAbortController);

      // Capture request context to detect stale responses
      const reqSessionId = sessionId;

      /** Release the claim, unless a newer send already took it over. */
      const releasePendingSend = () => {
         if (pendingSends.get(reqSessionId) === localAbortController)
            pendingSends.delete(reqSessionId);
      };

      // Get location using decision matrix (may prompt user if not yet requested)
      const location = await getLocationForChat();

      // Superseded while waiting on location: nothing has been pushed for this
      // turn yet, so there is nothing to unwind.
      if (localAbortController.signal.aborted) {
         releasePendingSend();
         return null;
      }

      const isFirstMessage = sessionMessages.value.length === 0;
      const userMessageTempId = sessionMessages.value.length > 0 ? sessionMessages.value[sessionMessages.value.length - 1].id + 1 : 1;
      const assistantTempId = userMessageTempId + 1;

      console.log('[chat-session-store] sendMessage called with:', { message: `${message.substring(0, 50)}...`, sessionId, options, location });

      // Named so the in-flight turn can own a copy: the placeholder has to
      // survive its thread leaving the screen, where `sessionMessages` won't.
      const assistantPlaceholder: ApalumaChatMessage = {
         id: assistantTempId,
         thread_id: sessionId,
         content: '',
         role: 'assistant',
         created_at: new Date().toISOString(),
         metadata: {},
         filter: null,
         refined_question: '',
         status: 'processing',
         isInitializedDone: false,
         followup_questions: null,
         number_of_documents: null,
         backend: 'luma2',
      };

      try {
         // add the temp user message and assistant message to the session messages
         sessionMessages.value.push({
            id: userMessageTempId,
            thread_id: sessionId,
            content: message,
            role: 'user',
            created_at: new Date().toISOString(),
            metadata: {},
            filter: null,
            refined_question: '',
            status: 'done',
            followup_questions: null,
            number_of_documents: null,
            backend: 'luma2',
         }, assistantPlaceholder);

         trackEvent('chat:ask', {
            message: message.substring(0, 1000),
            thread_id: sessionId,
            surface: options.surface,
            screen: options.screen,
            document_id: options.documentId ?? undefined,
         });

         const response = await apiSendMessage(sessionId, {
            content: message,
            role: 'user',
            metadata: {
               user_name: authStore.userProfile?.firstname || '',
               location: location ?? { latitude: '', longitude: '' },
               ...(options?.documentId && { document_id: options.documentId }),
               ...(options?.filename && { filename: options.filename }),
            },
         }, localAbortController.signal);

         // A turn the user replaced is dropped; a turn whose thread merely left
         // the screen is not — the answer still has to be persisted.
         if (localAbortController.signal.aborted) {
            releasePendingSend();
            removeTempMessages(reqSessionId, userMessageTempId, assistantTempId);
            return null;
         }

         // Replace temp user message with real response
         patchMessage(reqSessionId, userMessageTempId, () => response.user_message);

         // When WebSocket mode is active, stream the assistant response via WS
         // instead of relying on polling. The backend doesn't create an assistant
         // message in this mode, so we keep the temp placeholder and update it
         // when the WS answer arrives.
         console.log('[chat-session-store] Using WebSocket path');
         const turn: InFlightTurn = {
            token: nextTurnToken++,
            assistantTempId,
            message: {
               ...assistantPlaceholder,
               status: 'processing',
               isInitializedDone: true,
            },
            abort: localAbortController,
         };
         inFlight.set(reqSessionId, turn);
         releasePendingSend();
         patchMessage(reqSessionId, assistantTempId, () => turn.message);

         console.log('[chat-session-store] Calling wsChat.sendViaWebSocket, docids:', options?.documentId ? [options.documentId] : 'none');
         const { promise: wsPromise } = wsChat.sendViaWebSocket(
            message,
            sessionId,
            (partial) => {
               // The turn's own copy is canonical; the visible list mirrors it
               // only while its thread is the one on screen.
               turn.message = { ...turn.message, ...partial, isInitializedDone: true };
               patchMessage(reqSessionId, assistantTempId, () => turn.message);
            },
            options?.documentId ? [options.documentId] : undefined,
         );

         wsPromise
            .catch((err) => {
               console.error('WebSocket chat failed:', err);
               turn.message = { ...turn.message, status: 'error', isInitializedDone: true };
               patchMessage(reqSessionId, assistantTempId, () => turn.message);
            })
            .finally(() => {
               // Retire the slot only if a newer turn hasn't claimed it.
               if (inFlight.get(reqSessionId)?.token === turn.token)
                  inFlight.delete(reqSessionId);
            });

         // Auto-generate a title for new threads (first message) — fire and forget
         if (isFirstMessage) {
            generateThreadTitle(message).then((title) => {
               if (title) {
                  renameSession(sessionId, title);
               }
            }).catch(err => console.error('Failed to generate thread title:', err));
         }

         return response;
      }
      catch (e: any) {
         if (e?.name === 'AbortError' || localAbortController.signal.aborted) {
            // The abort superseded this turn mid-request. Its optimistic messages
            // are still on the list, and the replacement turn has pushed its own,
            // so they have to come off — the same unwind the post-request check
            // above performs when the abort lands a moment later.
            releasePendingSend();
            removeTempMessages(reqSessionId, userMessageTempId, assistantTempId);
            return null;
         }
         // Mark the assistant temp message as errored, on whichever thread it
         // belongs to — the failure is real whether or not it's on screen.
         releasePendingSend();
         inFlight.delete(reqSessionId);
         patchMessage(reqSessionId, assistantTempId, current => ({ ...current, error: true }));
         console.error(e);
         return null;
      }
   }

   /**
    * Retries sending a message to the AI chatbot
    * @param messageId - The ID of the message to retry
    * @returns The message content or refined question to retry
    */
   function retrySendMessage(messageId: number): string {
      const message = sessionMessages.value.find(m => m.id === messageId);

      if (!message) {
         return '';
      }

      // remove the already added user and assistant messages
      sessionMessages.value = sessionMessages.value.filter(m => m.id !== messageId && m.id !== messageId - 1);

      return message?.content ?? message?.refined_question ?? '';
   }

   /**
    * Stops a specific message request
    * @param messageId - The ID of the message to stop
    */
   function stopMessage(messageId: number) {
      // Cancelling is now something done to a specific turn. An id that matches
      // no live turn is a no-op — it used to cancel whatever was running, which
      // is how navigating away killed an answer mid-flight.
      const entry = [...inFlight.entries()].find(
         ([, turn]) => turn.assistantTempId === messageId,
      );
      if (!entry)
         return;

      const [threadId, turn] = entry;
      turn.message = { ...turn.message, stopped: true };
      patchMessage(threadId, messageId, current => ({ ...current, stopped: true }));
      wsChat.cancelThread(threadId);
   }

   /** Stop every running turn — for logout and teardown, not navigation. */
   function stopAllMessages() {
      wsChat.closeAll();
      inFlight.clear();
      pendingSends.forEach(controller => controller.abort());
      pendingSends.clear();
   }

   /**
    * Updates the feedback for a message.
    * @param messageId - The ID of the message to update
    * @param feedback - The feedback to set on the message
    */
   function setMessageFeedback(messageId: number, feedback: ChatMessageFeedback) {
      const message = sessionMessages.value.find(m => m.id === messageId);
      if (message) {
         message.feedback = feedback;
      }
   }

   /**
    * Resets the store to the initial state.
    */
   function $reset() {
      sessionsAbortController = null;
      sessionState.sessions = [];
      sessionState.total = 0;
      sessionState.offset = 0;
      sessionState.error = null;
      sessionState.isLoading = false;
      sessionState.titleFilter = '';

      sessionMessagesAbortController = null;
      stopAllMessages();
      floatingAssistantActive.value = false;
      clearSelectedSession();
   }

   return {
      // State
      sessionState,
      selectedSession,
      sessionMessages,
      sessionMessagesOffset,
      isSessionMessagesLoading,
      sessionMessagesError,
      floatingAssistantActive,

      // Getters
      hasMoreSessions,
      isSessionsLoaded,
      hasMoreSessionMessages,

      // Actions
      fetchSessions,
      searchSessions,
      loadMoreSessions,
      deleteSession,
      renameSession,
      fetchSessionMessages,
      loadMoreSessionMessages,
      selectSession,
      clearSelectedSession,
      syncFromRoute,
      createSession,
      updateSessionTitleOnFirstMessage,
      sendMessage,
      retrySendMessage,
      stopMessage,
      stopAllMessages,
      isThreadBusy,
      setMessageFeedback,
      $reset,
   };
});
